import { supabase } from '@/supabase.config';
import type { Permission, RoleAssignment, RoleFormValues, RoleOverview } from '../types';

function fail(error: { message: string; details?: string | null } | null): asserts error is null {
  if (error) throw new Error(error.details ? `${error.message}|${error.details}` : error.message);
}

export async function getRolesOverview(): Promise<RoleOverview[]> {
  const { data, error } = await supabase.rpc('get_roles_overview');
  fail(error);
  return data ?? [];
}

export async function getPermissionCatalog(): Promise<Permission[]> {
  const { data, error } = await supabase
    .from('permissions')
    .select('id, code, resource, action, description, is_scoped, module, module_order, resource_label, action_label')
    .order('module_order')
    .order('resource')
    .order('action');
  fail(error);
  return data ?? [];
}

export async function getRolePermissionCodes(roleId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('role_permissions')
    .select('permission:permissions(code)')
    .eq('role_id', roleId);
  fail(error);
  return (data ?? []).map((row) => row.permission.code);
}

/** Users holding the role, with the scope of each assignment (needs user.manage to see others) */
export async function getRoleAssignments(roleId: string): Promise<RoleAssignment[]> {
  const { data, error } = await supabase
    .from('user_roles')
    .select('id, scope, granted_at, user:users!user_roles_user_id_fkey(id, full_name, email), district:districts(id, name), region:regions(id, name)')
    .eq('role_id', roleId)
    .order('granted_at', { ascending: false });
  fail(error);
  return (data ?? []) as unknown as RoleAssignment[];
}

/** Creates a custom role; `copyFrom` copies another role's permission set */
export async function createRole(values: Omit<RoleFormValues, 'is_active'>, copyFrom?: string): Promise<string> {
  const { data, error } = await supabase.rpc('create_role', {
    p_code: values.code,
    p_name: values.name,
    p_description: values.description || undefined,
    p_copy_from: copyFrom,
  });
  fail(error);
  return data;
}

export async function updateRole(id: string, values: Pick<RoleFormValues, 'name' | 'description' | 'is_active'>): Promise<void> {
  const { error } = await supabase
    .from('roles')
    .update({ name: values.name.trim(), description: values.description.trim() || null, is_active: values.is_active })
    .eq('id', id);
  fail(error);
}

export async function deleteRole(id: string): Promise<void> {
  const { error } = await supabase.from('roles').delete().eq('id', id);
  fail(error);
}

export async function setRolePermissions(roleId: string, codes: string[]): Promise<void> {
  const { error } = await supabase.rpc('set_role_permissions', { p_role_id: roleId, p_perm_codes: codes });
  fail(error);
}
